import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { apiService, ApiError } from '../../services/api';
import { cn, formatDateTime, formatDate, downloadBlob } from '../../lib/utils';
import { Loader2, Send, FileText, CheckCircle2, Clock, AlertTriangle, RefreshCw, X, XCircle, ChevronUp, ChevronDown, Eye, Download, MoreVertical, ExternalLink, Link2, ShieldAlert } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';

interface StatusHistoryEntry {
  status: string;
  timestamp: number;
}

interface Letter {
  letterId: string;
  bureau: string;
  round?: number;
  status: string;
  statusHistory: StatusHistoryEntry[];
  lobTrackingUrl?: string;
  lobExpectedDeliveryDate?: string;
}

interface ReviewComment {
  id: string;
  comment: string;
  status: 'open' | 'closed';
  created_at: number;
  closed_at?: number;
  reviewer_id: string;
}

interface DocumentReview {
  documentId: string;
  documentName: string;
  status: string;
  reviewComments: ReviewComment[];
}

interface SlaInfo {
  targetHours: number;
  hoursSinceCreation: number;
  hoursSinceLastUpdate: number;
  status: 'on_track' | 'at_risk' | 'overdue' | 'paused';
}

interface DisputeForReview {
  disputeId: string;
  userId: string;
  disputeType: string;
  reason?: string;
  supportingReason?: string;
  disputeStatus: string;
  attachmentNames: string[];
  createdAt: number;
  updatedAt?: number;
  sla?: SlaInfo;
  user: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
  };
  documentReviews: DocumentReview[];
  letters: Letter[];
}

type SlaFilterValue = '' | 'on_track' | 'at_risk' | 'overdue' | 'paused';

interface DisputesReviewProps {
  refreshTrigger?: number;
}

export function DisputesReview({ refreshTrigger = 0 }: DisputesReviewProps) {
  const [disputes, setDisputes] = useState<DisputeForReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedLetterId, setSelectedLetterId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [pendingDisputeId, setPendingDisputeId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  
  const [downloadingLetterId, setDownloadingLetterId] = useState<string | null>(null);
  const [downloadingAllDisputeId, setDownloadingAllDisputeId] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [expandedRounds, setExpandedRounds] = useState<Record<string, Set<number>>>({});
  const [disputeResults, setDisputeResults] = useState<Record<string, { status: 'sending' | 'success' | 'error'; message?: string }>>({});
  const [slaFilter, setSlaFilter] = useState<SlaFilterValue>('');

  const fetchDisputes = async (page: number = currentPage, { silent = false, slaStatus = slaFilter }: { silent?: boolean; slaStatus?: SlaFilterValue } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError('');
      const response = await apiService.getDisputesForReview(page, limit, slaStatus || undefined);
      setDisputes(response.disputes || []);
      setTotalPages(response.pagination?.totalPages || 1);
      setTotal(response.pagination?.total || 0);
      setCurrentPage(page);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load disputes');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchDisputes();
  }, []);

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      fetchDisputes(currentPage, { silent: true });
    }, 120000);

    return () => clearInterval(refreshInterval);
  }, [currentPage]);

  useEffect(() => {
    if (refreshTrigger === 0) return;
    fetchDisputes(currentPage, { silent: true });
  }, [refreshTrigger]);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Auto-dismiss success after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleSlaFilterChange = (value: SlaFilterValue) => {
    setSlaFilter(value);
    setCurrentPage(1);
    fetchDisputes(1, { slaStatus: value });
  };

  const getSlaTargetHours = (): number | null => {
    for (const d of disputes) {
      if (d.sla?.targetHours) return d.sla.targetHours;
    }
    return null;
  };

  const handleViewLetter = async (letterId: string, userId: string) => {
    try {
      setLoadingPdf(true);
      setSelectedLetterId(letterId);
      setSelectedUserId(userId);
      const blob = await apiService.getDisputeLetterPdfAsManager(letterId);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load PDF');
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleDownloadLetter = async (letterId: string) => {
    try {
      setDownloadingLetterId(letterId);
      const blob = await apiService.getDisputeLetterPdfAsManager(letterId);
      downloadBlob(blob, `dispute-letter-${letterId}.pdf`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to download PDF');
    } finally {
      setDownloadingLetterId(null);
    }
  };

  const handleDownloadAllLetters = async (dispute: DisputeForReview) => {
    try {
      setDownloadingAllDisputeId(dispute.disputeId);
      const blob = await apiService.getAllDisputeLettersPdfAsManager(dispute.disputeId);
      const name = `${dispute.user.firstName}-${dispute.user.lastName}`;
      downloadBlob(blob, `${name}-all-letters.pdf`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to download letters');
    } finally {
      setDownloadingAllDisputeId(null);
    }
  };

  const handleSendDispute = async (disputeId: string) => {
    setPendingDisputeId(disputeId);
    setShowSendConfirm(true);
  };

  const confirmSendDispute = async () => {
    if (!pendingDisputeId) return;

    const disputeId = pendingDisputeId;
    setShowSendConfirm(false);
    setPendingDisputeId(null);
    setDisputeResults(prev => ({ ...prev, [disputeId]: { status: 'sending' } }));

    try {
      const response = await apiService.sendScheduledLetters(disputeId);

      if (response.data?.results) {
        const failedLetters = response.data.results.filter((r: any) => !r.success);
        if (failedLetters.length > 0) {
          const errorMessages = failedLetters
            .map((r: any) => `${r.bureau}: ${r.error}`)
            .join('; ');
          setDisputeResults(prev => ({ ...prev, [disputeId]: { status: 'error', message: errorMessages } }));
        } else {
          setDisputeResults(prev => ({ ...prev, [disputeId]: { status: 'success', message: response.message || 'All letters sent successfully!' } }));
        }
      } else {
        setDisputeResults(prev => ({ ...prev, [disputeId]: { status: 'success', message: response.message || 'Letters sent successfully!' } }));
      }
    } catch (err) {
      let errorMessage = 'Failed to send letters';
      if (err instanceof ApiError && err.details) {
        try {
          const details = typeof err.details === 'string' ? JSON.parse(err.details) : err.details;
          if (details.results && Array.isArray(details.results)) {
            errorMessage = details.results
              .filter((r: any) => !r.success && r.error)
              .map((r: any) => `${r.bureau}: ${r.error}`)
              .join('; ');
          } else {
            errorMessage = err.message;
          }
        } catch {
          errorMessage = err.message;
        }
      } else if (err instanceof ApiError) {
        errorMessage = err.message;
      }
      setDisputeResults(prev => ({ ...prev, [disputeId]: { status: 'error', message: errorMessage } }));
    }
  };

  useEffect(() => {
    const successIds = Object.entries(disputeResults)
      .filter(([, r]) => r.status === 'success')
      .map(([id]) => id);

    if (successIds.length === 0) return;

    const timer = setTimeout(() => {
      setDisputes(prev => prev.filter(d => !successIds.includes(d.disputeId)));
      setDisputeResults(prev => {
        const next = { ...prev };
        successIds.forEach(id => delete next[id]);
        return next;
      });
      setTotal(prev => Math.max(0, prev - successIds.length));
    }, 5000);

    return () => clearTimeout(timer);
  }, [disputeResults]);

  const handleClosePdf = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setSelectedLetterId(null);
    setSelectedUserId(null);
  };

  const getStatusBadge = (status: string) => {
    if (!status) return null;
    const baseClasses = 'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium';
    switch (status.toLowerCase()) {
      case 'success':
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            <CheckCircle2 className="h-3 w-3" />
            Success
          </span>
        );
      case 'scheduled':
        return (
          <span className={`${baseClasses} bg-orange-100 text-orange-800`}>
            <Clock className="h-3 w-3" />
            Waiting For Review
          </span>
        );
      case 'mailed':
        return (
          <span className={`${baseClasses} bg-violet-100 text-violet-800`}>
            <Send className="h-3 w-3" />
            Mailed
          </span>
        );
      case 'delivered':
        return (
          <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
            <CheckCircle2 className="h-3 w-3" />
            Delivered
          </span>
        );
      case 'failed':
      case 'returned':
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            <AlertTriangle className="h-3 w-3" />
            Failed
          </span>
        );
      case 'new':
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            <Clock className="h-3 w-3" />
            Draft
          </span>
        );
      case 'sent':
        return (
          <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
            <CheckCircle2 className="h-3 w-3" />
            Sent For Mailing
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {status}
          </span>
        );
    }
  };

  const getDisputeReason = (dispute: DisputeForReview): string => {
    const reason = dispute.reason || dispute.supportingReason;
    return reason?.trim() || '—';
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="w-full -mt-5 space-y-4">
      {error && (
        <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md flex items-start justify-between gap-4">
          <p className="whitespace-pre-wrap flex-1">{error}</p>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 hover:bg-red-100"
            onClick={() => setError('')}
          >
            <X className="h-4 w-4 text-red-600" />
          </Button>
        </div>
      )}

      {success && (
        <div className="p-3 text-sm text-green-600 bg-green-50 border border-green-200 rounded-md">
          {success}
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold text-foreground">Disputes Pending Review <span className="text-gray-500 font-normal">({total})</span></h1>
          {getSlaTargetHours() && (
            <span className="text-xs text-muted-foreground bg-gray-100 px-2 py-1 rounded-md">
              {getSlaTargetHours()}h SLA target
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 bg-background p-0.5">
            {([
              { value: '' as SlaFilterValue, label: 'All' },
              { value: 'overdue' as SlaFilterValue, label: 'Overdue', dotColor: 'bg-red-500' },
              { value: 'at_risk' as SlaFilterValue, label: 'At Risk', dotColor: 'bg-amber-500' },
              { value: 'on_track' as SlaFilterValue, label: 'On Track', dotColor: 'bg-green-500' },
              { value: 'paused' as SlaFilterValue, label: 'Paused', dotColor: 'bg-slate-400' },
            ]).map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleSlaFilterChange(opt.value)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  slaFilter === opt.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {opt.dotColor && <span className={`w-1.5 h-1.5 rounded-full ${opt.dotColor}`} />}
                {opt.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchDisputes(currentPage)}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {disputes.length === 0 ? (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
          <div className="text-center py-16">
            <div className="flex items-center justify-center mx-auto mb-4 bg-gray-100" style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
              <FileText className="h-5 w-5 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Disputes Pending Review</h3>
            <p className="text-sm text-gray-500">All disputes have been reviewed. Check back later.</p>
          </div>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-gray-200">
                <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Client</TableHead>
                <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Type</TableHead>
                <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Created</TableHead>
                <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">SLA</TableHead>
                <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Bureaus</TableHead>
                <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Round</TableHead>
                <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500">Status</TableHead>
                <TableHead className="py-2.5 px-4 font-medium text-xs text-gray-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {disputes.map((dispute) => {
                const maxRound = Math.max(...dispute.letters.map(l => l.round || 1));
                const currentStatus = dispute.disputeStatus || 'new';
                const isExpanded = expandedRows[dispute.disputeId];
                const bureaus = Array.from(new Set(dispute.letters.map(l => l.bureau))).filter(Boolean);
                const rowResult = disputeResults[dispute.disputeId];
                const isSending = rowResult?.status === 'sending';
                const isSent = rowResult?.status === 'success';
                const isRowDisabled = isSending || isSent;
                const hasDocumentIssues = dispute.documentReviews?.some(
                  dr => dr.status === 'rejected' || dr.reviewComments?.some(c => c.status === 'open')
                );

                return (
                  <React.Fragment key={dispute.disputeId}>
                    {rowResult && rowResult.status !== 'sending' && (
                      <TableRow className="hover:bg-transparent border-0">
                        <TableCell colSpan={8} className="px-4 py-0 pb-0 pt-2 border-0">
                          <div className={cn(
                            'px-3 py-2 text-sm rounded-md flex items-center gap-2',
                            rowResult.status === 'success'
                              ? 'bg-green-50 text-green-700 border border-green-200'
                              : 'bg-red-50 text-red-700 border border-red-200'
                          )}>
                            {rowResult.status === 'success' ? (
                              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                            ) : (
                              <XCircle className="h-4 w-4 flex-shrink-0" />
                            )}
                            <span>{rowResult.message}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow
                      className={cn(
                        'transition-colors cursor-pointer',
                        isRowDisabled ? 'opacity-50 pointer-events-none' : 'hover:bg-gray-50/50',
                        isExpanded ? 'bg-gray-50 border-b-0' : 'border-b border-gray-200'
                      )}
                      onClick={isRowDisabled ? undefined : () => setExpandedRows(prev => ({ ...prev, [dispute.disputeId]: !prev[dispute.disputeId] }))}
                    >
                      <TableCell className="pl-4 pr-2 py-3">
                        <a
                          href={`/admin?userId=${dispute.userId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 font-medium text-gray-900 hover:text-gray-700 underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {dispute.user.firstName} {dispute.user.lastName}
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                        <div className="text-xs text-gray-500 mt-0.5">{dispute.user.phoneNumber}</div>
                      </TableCell>
                      <TableCell className="px-2 py-3 text-gray-900 capitalize">{dispute.disputeType}</TableCell>
                      <TableCell className="px-2 py-3 text-gray-900">{formatDateTime(dispute.createdAt)}</TableCell>
                      <TableCell className="px-2 py-3">
                        {dispute.sla ? (() => {
                          const { status, hoursSinceCreation, hoursSinceLastUpdate } = dispute.sla;
                          const slaConfig: Record<string, { label: string; bg: string; text: string; border: string; dot: string }> = {
                            on_track: { label: 'On Track', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', dot: 'bg-green-500' },
                            at_risk: { label: 'At Risk', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
                            overdue: { label: 'Overdue', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
                            paused: { label: 'Waiting for user', bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', dot: 'bg-slate-400' },
                          };
                          const cfg = slaConfig[status] || slaConfig.on_track;
                          const isPaused = status === 'paused';
                          return (
                            <div className="inline-flex flex-col gap-1">
                              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                                {cfg.label}
                              </span>
                              <span className="text-[11px] text-muted-foreground whitespace-nowrap">
                                {isPaused ? `${hoursSinceLastUpdate}h since last action` : `${hoursSinceCreation}h ago`}
                              </span>
                            </div>
                          );
                        })() : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="px-2 py-3">
                        <div className="flex items-center gap-1">
                          {bureaus.map((bureau, idx) => {
                            const lower = bureau.toLowerCase();
                            const abbr = lower.includes('transunion') ? 'TU'
                              : lower.includes('experian') ? 'EX'
                              : lower.includes('equifax') ? 'EQ'
                              : bureau.length > 10 ? bureau.substring(0, 10) + '...' : bureau;
                            return (
                              <span key={idx} className="text-xs font-semibold px-1.5 py-0.5 bg-gray-100 rounded" title={bureau}>
                                {abbr}
                              </span>
                            );
                          })}
                        </div>
                      </TableCell>
                      <TableCell className="px-2 py-3">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-white border border-gray-200 text-gray-800">
                          {maxRound > 1 ? `Rounds 1–${maxRound}` : 'Round 1'}
                        </span>
                      </TableCell>
                      <TableCell className="px-2 py-3">
                        <div className="flex items-center gap-2">
                          {getStatusBadge(currentStatus)}
                          {hasDocumentIssues && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200" title="Documents have open review comments">
                              <ShieldAlert className="h-2.5 w-2.5" />
                              Docs
                            </span>
                          )}
                          {isSending && <Loader2 className="h-4 w-4 animate-spin text-gray-500" />}
                        </div>
                      </TableCell>
                      <TableCell className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" disabled={isRowDisabled}>
                                {downloadingAllDisputeId === dispute.disputeId ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <MoreVertical className="h-4 w-4" />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56 z-50">
                              <DropdownMenuItem
                                onClick={() => handleSendDispute(dispute.disputeId)}
                              >
                                <Send className="h-4 w-4 mr-2" />
                                Send Letters
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={
                                  downloadingAllDisputeId === dispute.disputeId
                                    ? undefined
                                    : () => handleDownloadAllLetters(dispute)
                                }
                                className={
                                  downloadingAllDisputeId === dispute.disputeId
                                    ? 'opacity-50 cursor-not-allowed'
                                    : ''
                                }
                              >
                                {downloadingAllDisputeId === dispute.disputeId ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Downloading...
                                  </>
                                ) : (
                                  <>
                                    <Download className="h-4 w-4 mr-2" />
                                    Download All PDFs
                                  </>
                                )}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Row */}
                    {isExpanded && (() => {
                      const lettersByRound = new Map<number, Letter[]>();
                      dispute.letters.forEach(letter => {
                        const round = letter.round || 1;
                        if (!lettersByRound.has(round)) lettersByRound.set(round, []);
                        lettersByRound.get(round)!.push(letter);
                      });
                      const rounds = Array.from(lettersByRound.keys()).sort((a, b) => a - b);
                      const disputeExpandedRounds = expandedRounds[dispute.disputeId] || new Set([Math.max(...rounds)]);

                      const toggleRound = (round: number) => {
                        setExpandedRounds(prev => {
                          const current = new Set(prev[dispute.disputeId] || [Math.max(...rounds)]);
                          if (current.has(round)) current.delete(round);
                          else current.add(round);
                          return { ...prev, [dispute.disputeId]: current };
                        });
                      };

                      const statusConfig: Record<string, { label: string; className: string }> = {
                        'new': { label: 'Draft', className: 'text-gray-500' },
                        'scheduled': { label: 'Pending Review', className: 'text-orange-600' },
                        'sent': { label: 'Sent For Mailing', className: 'text-blue-600' },
                        'mailed': { label: 'Mailed', className: 'text-violet-600' },
                        'in_transit': { label: 'Mailed', className: 'text-violet-600' },
                        'delivered': { label: 'Delivered', className: 'text-blue-600' },
                        'success': { label: 'Resolved', className: 'text-green-600' },
                        'failed': { label: 'Failed', className: 'text-red-600' },
                        'returned': { label: 'Failed', className: 'text-red-600' },
                      };

                      return (
                      <TableRow className="border-t-0">
                        <TableCell colSpan={8} className="bg-gray-50 px-4 pt-1 pb-4">
                          <div className="space-y-3">
                            <div className="bg-white border border-gray-200 rounded-lg px-6 py-3">
                              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Reason</div>
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">{getDisputeReason(dispute)}</p>
                            </div>
                            {rounds.map((round) => {
                              const roundLetters = lettersByRound.get(round) || [];
                              const isRoundExpanded = disputeExpandedRounds.has(round);

                              const roundStatuses = roundLetters.map(l => l.status?.toLowerCase() || 'new');
                              const roundOverallStatus = roundStatuses.length > 0
                                ? (roundStatuses.some(s => s === 'success') ? 'success'
                                  : roundStatuses.some(s => s === 'delivered') ? 'delivered'
                                  : roundStatuses.some(s => ['mailed', 'in_transit'].includes(s)) ? 'mailed'
                                  : roundStatuses.some(s => s === 'sent') ? 'sent'
                                  : roundStatuses.some(s => s === 'scheduled') ? 'scheduled'
                                  : roundStatuses.some(s => ['failed', 'returned'].includes(s)) ? 'failed'
                                  : 'new')
                                : 'new';
                              const roundStatusLabel = statusConfig[roundOverallStatus]?.label || 'Draft';
                              const roundStatusClass = statusConfig[roundOverallStatus]?.className || 'text-gray-500';

                              return (
                              <div key={round} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                <div
                                  className="flex items-center justify-between px-6 py-2.5 border-b border-gray-200 cursor-pointer hover:bg-gray-50 select-none"
                                  onClick={() => toggleRound(round)}
                                >
                                  <div className="flex items-center gap-2">
                                    {isRoundExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    <span className="text-sm font-medium">Round {round}</span>
                                    <span className="text-xs text-gray-400">({roundLetters.length} {roundLetters.length === 1 ? 'letter' : 'letters'})</span>
                                  </div>
                                  <span className={`text-xs font-medium ${roundStatusClass}`}>{roundStatusLabel}</span>
                                </div>

                                {isRoundExpanded && (
                                  <>
                                    {/* Letters Section */}
                                    <div className="px-6 pt-4 pb-4">
                                      <h4 className="text-sm font-medium mb-3">Letters</h4>
                                      <div className="flex items-center gap-4 flex-wrap">
                                        {roundLetters.map((letter) => {
                                          const letterStatus = letter.status || 'new';
                                          const config = statusConfig[letterStatus.toLowerCase()] || { label: letterStatus, className: 'text-gray-500' };
                                          return (
                                          <div key={letter.letterId} className="flex items-center gap-2 bg-gray-50 px-4 py-2.5 rounded-lg">
                                            <FileText className="h-4 w-4 text-gray-700" />
                                            <div className="flex flex-col">
                                              <span className="text-sm text-gray-900 font-medium">{letter.bureau}</span>
                                              <span className={`text-[11px] leading-tight ${config.className}`}>{config.label}</span>
                                              {letter.lobExpectedDeliveryDate && letterStatus === 'mailed' && (
                                                <span className="text-[10px] text-gray-400">
                                                  ETA: {formatDate(letter.lobExpectedDeliveryDate)}
                                                </span>
                                              )}
                                            </div>
                                            {letter.lobTrackingUrl && (
                                              <a
                                                href={letter.lobTrackingUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="h-6 w-6 flex items-center justify-center hover:bg-gray-200 rounded"
                                                title="Track letter"
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                <Link2 className="h-3.5 w-3.5 text-gray-500" />
                                              </a>
                                            )}
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              onClick={() => handleDownloadLetter(letter.letterId)}
                                              className="h-6 w-6 p-0 hover:bg-gray-200 rounded"
                                              disabled={downloadingLetterId === letter.letterId}
                                            >
                                              {downloadingLetterId === letter.letterId ? (
                                                <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                                              ) : (
                                                <Download className="h-4 w-4 text-gray-700" />
                                              )}
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              onClick={() => handleViewLetter(letter.letterId, dispute.userId)}
                                              className="h-6 w-6 p-0 hover:bg-gray-200 rounded"
                                              disabled={loadingPdf && selectedLetterId === letter.letterId}
                                            >
                                              {loadingPdf && selectedLetterId === letter.letterId ? (
                                                <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                                              ) : (
                                                <Eye className="h-4 w-4 text-gray-700" />
                                              )}
                                            </Button>
                                          </div>
                                          );
                                        })}
                                      </div>
                                    </div>

                                    {/* Timeline for this round */}
                                    {(() => {
                                      const roundTerminalStatuses = roundLetters.map(l => l.status?.toLowerCase() || 'new');
                                      const isRoundFailed = roundTerminalStatuses.some(s => s === 'failed' || s === 'returned');
                                      const terminalStep = isRoundFailed
                                        ? { key: 'failed', label: 'Failed' }
                                        : { key: 'success', label: 'Success' };

                                      const timelineSteps = [
                                        { key: 'new', label: 'Created' },
                                        { key: 'scheduled', label: 'Waiting For Review' },
                                        { key: 'sent', label: 'Sent For Mailing' },
                                        { key: 'mailed', label: 'Mailed' },
                                        { key: 'delivered', label: 'Delivered' },
                                        terminalStep,
                                      ];

                                      const completedMap = new Map<string, number>();
                                      const normalizeStatus = (s: string) => s === 'in_transit' ? 'mailed' : s;
                                      const setEarliest = (key: string, ts: number) => {
                                        const existing = completedMap.get(key);
                                        if (!existing || ts < existing) completedMap.set(key, ts);
                                      };

                                      completedMap.set('new', dispute.createdAt);
                                      roundLetters.forEach(letter => {
                                        letter.statusHistory?.forEach(sh => setEarliest(normalizeStatus(sh.status), sh.timestamp));
                                      });

                                      return (
                                      <div className="px-6 pb-6">
                                        <h4 className="text-sm font-medium mb-3">Timeline</h4>
                                        <div className="relative">
                                          <div className="absolute top-[6px] left-[6px] h-0.5 bg-gray-300" style={{ width: `calc(${timelineSteps.length - 1} * (120px + 40px))` }}></div>
                                          <div className="absolute top-[6px] left-[6px] h-0.5 bg-black transition-all" style={{ width: `calc(${Math.max(0, Array.from(completedMap.keys()).filter(k => timelineSteps.some(s => s.key === k)).length - 1)} * (120px + 40px))` }}></div>

                                          <div className="flex items-start" style={{ gap: '120px' }}>
                                            {timelineSteps.map((step) => {
                                              const ts = completedMap.get(step.key);
                                              const isCompleted = !!ts;
                                              const dateStr = ts ? formatDateTime(ts).split(',')[0] : '-';

                                              return (
                                                <div key={step.key} className="flex flex-col relative z-10" style={{ width: '40px' }}>
                                                  <div className={`w-[12px] h-[12px] rounded-full border-[3px] ${isCompleted ? 'border-black bg-white' : 'border-gray-300 bg-white'}`}></div>
                                                  <span className={`text-xs mt-2 whitespace-nowrap ${isCompleted ? 'font-medium text-gray-900' : 'text-gray-400'}`}>{step.label}</span>
                                                  <span className={`text-xs whitespace-nowrap ${isCompleted ? 'text-gray-500' : 'text-gray-400'}`}>{dateStr}</span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                        {isRoundFailed && (
                                          <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                            <Clock className="h-3 w-3" />
                                            This round has ended — ready to start the next round
                                          </div>
                                        )}
                                      </div>
                                      );
                                    })()}
                                  </>
                                )}
                              </div>
                              );
                            })}

                            {/* Documents Section (shared across rounds) */}
                            {dispute.attachmentNames.length > 0 && (
                              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden px-6 py-4">
                                <h4 className="text-sm font-medium mb-3">
                                  Attached Documents
                                  {hasDocumentIssues && (
                                    <span className="ml-2 text-xs font-normal text-amber-600">(has open comments)</span>
                                  )}
                                </h4>
                                <div className="flex items-center gap-4 flex-wrap">
                                  {dispute.attachmentNames.map((docName) => {
                                    const review = dispute.documentReviews?.find(dr => dr.documentName === docName);
                                    const docStatus = review?.status || 'unknown';
                                    const openComments = review?.reviewComments?.filter(c => c.status === 'open') || [];
                                    const docStatusConfig: Record<string, { label: string; className: string }> = {
                                      'uploaded': { label: 'Pending Review', className: 'text-gray-900' },
                                      'approved': { label: 'Approved', className: 'text-gray-900' },
                                      'rejected': { label: 'Needs Re-upload', className: 'text-orange-600' },
                                      'reuploaded': { label: 'Re-uploaded', className: 'text-gray-900' },
                                      'expired': { label: 'Expired', className: 'text-orange-600' },
                                    };
                                    const sConfig = docStatusConfig[docStatus] || { label: 'No Review', className: 'text-gray-400' };

                                    return (
                                      <div key={docName} className="flex items-center gap-2">
                                        <FileText className="h-4 w-4 text-gray-700" />
                                        <span className="text-sm font-medium text-gray-900">{docName}</span>
                                        <span className={`text-[11px] ${sConfig.className}`}>{sConfig.label}</span>
                                        {openComments.length > 0 && (
                                          <span className="text-[10px] text-red-600 font-medium">
                                            ({openComments.length} open)
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })()}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {(currentPage - 1) * limit + 1}-{Math.min(currentPage * limit, total)} of {total} disputes
          </div>

          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => fetchDisputes(currentPage - 1)}
              disabled={currentPage === 1 || loading}
              className="h-9 text-sm"
            >
              Previous
            </Button>

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <Button
                    key={pageNum}
                    size="sm"
                    variant="ghost"
                    onClick={() => fetchDisputes(pageNum)}
                    disabled={loading}
                    className={cn(
                      "h-9 w-9 text-sm rounded-lg",
                      currentPage === pageNum
                        ? "border border-gray-300 bg-white text-gray-900 font-medium hover:bg-gray-50"
                        : "text-gray-600 hover:bg-gray-50"
                    )}
                  >
                    {pageNum}
                  </Button>
                );
              })}

              {totalPages > 5 && currentPage < totalPages - 2 && (
                <>
                  <span className="px-2 text-muted-foreground">...</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => fetchDisputes(totalPages)}
                    disabled={loading}
                    className="h-9 w-9 text-sm"
                  >
                    {totalPages}
                  </Button>
                </>
              )}
            </div>

            <Button
              size="sm"
              variant="ghost"
              onClick={() => fetchDisputes(currentPage + 1)}
              disabled={currentPage >= totalPages || loading}
              className="h-9 text-sm"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <Dialog open={!!pdfUrl} onOpenChange={handleClosePdf}>
        <DialogContent
          className="!h-screen !max-w-none !translate-x-0 !translate-y-0 !top-0 !left-auto !right-0 !rounded-none !data-[state=closed]:slide-out-to-right !data-[state=open]:slide-in-from-right !flex !flex-col !justify-start !items-start !p-0 !gap-0 [&>button]:!right-6 [&>button]:!top-[14px]"
          style={{ width: '1100px' }}
          aria-describedby="pdf-viewer-description"
        >
          <DialogHeader className="flex-shrink-0 w-full border-b" style={{ padding: '16px 24px' }}>
            <DialogTitle className="text-lg font-semibold">Letter Preview</DialogTitle>
          </DialogHeader>
          <div id="pdf-viewer-description" className="sr-only">
            Preview of the dispute letter PDF document
          </div>
          {loadingPdf ? (
            <div className="flex items-center justify-center flex-1 w-full">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : pdfUrl ? (
            <div className="flex-1 w-full min-h-0" style={{ padding: '16px 24px' }}>
              <iframe
                src={pdfUrl}
                className="w-full h-full border rounded-md"
                title="Letter PDF Preview"
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

  <Dialog open={showSendConfirm} onOpenChange={setShowSendConfirm}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          Send Physical Letters?
        </DialogTitle>
        <DialogDescription className="space-y-2 pt-2">
          <p>
            Are you sure you want to send these letters? This will mail physical letters to credit bureaus via Lob.
          </p>
          <p className="text-amber-600 font-medium">
            This action cannot be undone.
          </p>
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="gap-2">
        <Button
          variant="outline"
          onClick={() => {
            setShowSendConfirm(false);
            setPendingDisputeId(null);
          }}
        >
          Cancel
        </Button>
        <Button onClick={confirmSendDispute}>
          <Send className="h-4 w-4 mr-2" />
          Yes, Send Letters
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

</div>
  );
}

